import { queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  ArrowDownCircleIcon,
  ArrowUpCircleIcon,
  LoaderCircleIcon,
  MinusCircleIcon,
  type LucideIcon,
} from 'lucide-react';
import z from 'zod';

import { PageTitle } from '@components/PageTitle';
import { Pagination } from '@components/ui/pagination';
import { EQueryKeys } from '@enums/queryKeys.enum';
import { dateFormatter } from '@formatters/date.formatter';
import { cn } from '@lib/utils';
import type { Statement } from '@models/statement.model';
import { collaboratorService } from '@services/collaborators/service';

const myStatementsQueryOptions = (page: number) =>
  queryOptions({
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60,
    queryKey: [EQueryKeys.MyStatements, page],
    queryFn: () =>
      collaboratorService.getStatements({
        page,
      }),
  });

export const Route = createFileRoute('/_layout/(private)/my-statements')({
  component: MyStatements,
  validateSearch: search =>
    z
      .object({
        page: z.number().int().optional().default(1),
      })
      .parse(search),
  loaderDeps: ({ search }) => ({
    page: search.page,
  }),
  loader: async ({ context, deps }) => {
    await context.queryClient.ensureQueryData(
      myStatementsQueryOptions(deps.page),
    );
  },
  pendingComponent: () => (
    <div className="flex min-h-dvh w-full items-center justify-center">
      <LoaderCircleIcon className="text-primary animate-spin" size={80} />
    </div>
  ),
});

function MyStatements() {
  const { page } = Route.useSearch();

  const { data } = useQuery(myStatementsQueryOptions(page));
  console.log({ data });
  return (
    <div className="w-full space-y-3">
      <PageTitle title="Meu extrato de pontos" />

      <div className="grid w-full rounded-xl border-zinc-200 bg-zinc-200 shadow">
        {data?.list.map(statement => (
          <StatementItem
            icon={iconsByType[statement.type]}
            key={statement.id}
            statement={statement}
          />
        ))}
      </div>
      <Pagination
        currentPage={data?.pagination.page}
        totalPages={data?.pagination.totalPages}
      />
    </div>
  );
}

type StatementItemProps = {
  icon: LucideIcon;
  statement: Statement;
};

function StatementItem({ icon: Icon, statement }: StatementItemProps) {
  const getLabelByType = labelByType[statement.type];

  const label = getLabelByType(statement.amount);
  const dateFormatted = dateFormatter(statement.createdAt, 'dd/MM/yyyy');

  return (
    <div className="even:bg-background flex min-h-15 items-center justify-between px-6">
      <Icon
        className={cn('text-secondary', {
          'text-green-600': statement.type === 'ENTRADA',
          'text-red-400': statement.type === 'SAIDA',
        })}
        size={22}
        strokeWidth={2}
      />
      <span className="ml-4 font-medium text-gray-500">{label}</span>

      <span className="ml-auto text-gray-500">{dateFormatted}</span>
    </div>
  );
}

const iconsByType: Record<string, LucideIcon> = {
  SAIDA: ArrowDownCircleIcon,
  ENTRADA: ArrowUpCircleIcon,
  SALDO: MinusCircleIcon,
};

const labelByType: Record<string, (amount: number) => string> = {
  SAIDA: amount => `Saída de ${amount} pontos`,
  ENTRADA: amount => `Entrada de ${amount} pontos`,
  SALDO: amount => `Saldo de ${amount} pontos`,
};
